import { useQuery } from "@tanstack/react-query";
import { Pencil, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTRPC } from "@/trpc";
import { getProviderSpecialtyCodes } from "../lib/provider-specialties";
import type { Credential } from "../types";

export function ProviderSpecialtyChips({
  codes,
  prominent = false,
}: {
  codes: string[];
  prominent?: boolean;
}) {
  const trpc = useTRPC();
  const specialties = useQuery(
    trpc.healthcareProfessionalTypes.getByCodes.queryOptions(
      { codes },
      { enabled: codes.length > 0 },
    ),
  );
  if (codes.length === 0) {
    return null;
  }
  if (specialties.isLoading) {
    return (
      <p role="status" className="text-sm text-slate-500">
        Loading specialties…
      </p>
    );
  }
  const labels = new Map(
    (specialties.data ?? []).map((specialty) => [
      specialty.code,
      specialty.name,
    ]),
  );

  return (
    <div className="space-y-2">
      <ul aria-label="Selected specialties" className="flex flex-wrap gap-2">
        {codes.map((code) => (
          <li
            key={code}
            className={cn(
              "rounded-xl border border-primary/10 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary",
              prominent && "px-4 py-2.5 text-sm",
            )}
          >
            {labels.get(code) ?? code}
          </li>
        ))}
      </ul>
      {specialties.isError && (
        <p role="alert" className="text-xs text-slate-500">
          Some specialty names could not be loaded. Your selections are still
          shown.
        </p>
      )}
    </div>
  );
}

export function ProviderSpecialtiesSummary({
  credentials,
  onEdit,
}: {
  credentials: Credential[];
  onEdit?: () => void;
}) {
  const codes = getProviderSpecialtyCodes(credentials);

  return (
    <section
      aria-label="Specialties"
      className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="inline-flex items-center gap-2 text-base font-bold tracking-tight text-slate-900">
          <Stethoscope className="size-4 text-primary" aria-hidden="true" />
          Specialties
        </h2>
        {onEdit && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 rounded-xl px-2 text-xs font-semibold text-primary hover:bg-primary/5 hover:text-primary"
            onClick={onEdit}
          >
            <Pencil className="size-3" aria-hidden="true" />
            Edit specialties
          </Button>
        )}
      </div>
      {codes.length > 0 ? (
        <ProviderSpecialtyChips codes={codes} />
      ) : (
        <p className="text-sm leading-relaxed text-slate-500">
          No specialties on file.
        </p>
      )}
    </section>
  );
}
